import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { observer } from "mobx-react-lite";
import { documentTableRenderFlags } from "./table/documentTablePresentation";
import type { DocumentListTableProps } from "./table/documentTableTypes";
import { TableLayout } from "./table/TableLayout";
import { useDocumentTableState } from "./table/useDocumentTableState";
export type { DocumentWithTags, UnifiedSearchRow } from "./table/documentTableTypes";
export const DocumentListTable = observer(function DocumentListTable(
  props: DocumentListTableProps
) {
  const state = useDocumentTableState(props);
  const { store, displayDocuments, folders, hasRenderedTable, setHasRenderedTable, className, t } =
    state;

  // Loading state - only show full-page spinner on the very first load.
  // Once the table has rendered, keep the header visible to preserve search input focus.
  if (isInitialTableLoad(state)) {
    return (
      <div
        className={cn(
          "flex items-center justify-center rounded-lg border bg-muted/10 p-12",
          className
        )}
      >
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{t.documents.table.loadingDocuments}</p>
        </div>
      </div>
    );
  }

  // Error state (only when store is managing data)
  if (store?.error) {
    return (
      <div
        className={cn(
          "flex items-center justify-center rounded-lg border border-destructive/50 bg-destructive/5 p-12",
          className
        )}
      >
        <div className="flex flex-col items-center gap-3">
          <p className="text-sm font-medium text-destructive">{t.documents.table.loadError}</p>
          <p className="text-xs text-muted-foreground">{store.error}</p>
        </div>
      </div>
    );
  }
  const presentation = documentTableRenderFlags(state);
  if (!hasRenderedTable) setHasRenderedTable(true);
  return <TableLayout {...state} {...presentation} />;
});

function isInitialTableLoad(state: ReturnType<typeof useDocumentTableState>) {
  return (
    state.store?.isLoading &&
    state.displayDocuments.length === 0 &&
    state.folders.length === 0 &&
    !state.hasRenderedTable
  );
}
